import { Box, Flex } from '@chakra-ui/react';
import { TabsList } from '@/componentsV1/Tabs/TabList';
import { CandidateItem } from './searchItems/CandidateItem';
import { JobItem } from './searchItems/JobItem';
import { FlowItem } from './searchItems/FlowItem';
import { InterviewItem } from './searchItems/InterviewItem';
import { KCenterSpinner } from '@/ui-library/components/atoms/KCenterSpinner/KCenterSpinner';
import { NoDataFound } from './NoDataFound';
import { TAB_ID } from '../constants';

interface SearchResultsViewProps {
  tabItems: any[];
  selectedTab: any;
  onTabSelect: (_tab: any) => void;
  searchResults: any;
  flows: any[] | null;
  flowsIsLoading: boolean;
  summaryInProgress: boolean;
  selectedIndex: number;
  hasError: boolean;
  onSearchClose: () => void;
}

const NO_RESULTS = (
  <NoDataFound
    label="No results found"
    subLabel="Sorry, there are no results for your search. Try again with a different keyword."
  />
);

const ERROR_RESULT = (
  <NoDataFound
    label="Something went wrong"
    subLabel="Please try again later or contact support@kula.ai"
  />
);

export const SearchResultsView = ({
  tabItems,
  selectedTab,
  onTabSelect,
  searchResults,
  flows,
  flowsIsLoading,
  summaryInProgress,
  selectedIndex,
  hasError,
  onSearchClose,
}: SearchResultsViewProps) => {
  return (
    <>
      <Flex h="auto" w="100%" pt="8px">
        <TabsList
          enableCount={true}
          containerStyles={{ width: '100%' }}
          items={tabItems.map((item) => ({
            ...item,
            isSelected: item.id === selectedTab.id,
          }))}
          selectedItem={{
            ...selectedTab,
            count: tabItems.find((t) => t.id === selectedTab.id)?.count || 0,
            isSelected: true,
          }}
          onTabSelection={onTabSelect}
        />
      </Flex>

      <Flex
        h="calc(100% - 50px)"
        className="ai-search-results"
        overflowY="auto"
        w="100%"
        flex="1"
      >
        {hasError ? (
          <Flex
            justify="center"
            align="center"
            w="100%"
            h="100%"
            flex="1"
            mt="50px"
          >
            {ERROR_RESULT}
          </Flex>
        ) : (
          <Box pt="10px" w="100%">
            {selectedTab.id === TAB_ID.CANDIDATES && (
              <CandidatesTab
                candidates={searchResults?.candidates}
                summaryInProgress={summaryInProgress}
                selectedIndex={selectedIndex}
                onSearchClose={onSearchClose}
              />
            )}

            {selectedTab.id === TAB_ID.JOBS && (
              <JobsTab
                jobs={searchResults?.jobs}
                selectedIndex={selectedIndex}
                onSearchClose={onSearchClose}
              />
            )}

            {selectedTab.id === TAB_ID.INTERVIEWS && (
              <InterviewsTab
                interviews={searchResults?.interviews}
                selectedIndex={selectedIndex}
                onSearchClose={onSearchClose}
              />
            )}

            {selectedTab.id === TAB_ID.FLOWS && (
              <FlowsTab
                flows={flows}
                isLoading={flowsIsLoading}
                selectedIndex={selectedIndex}
                onSearchClose={onSearchClose}
              />
            )}
          </Box>
        )}
      </Flex>
    </>
  );
};

// --------------- Tab Content Components ---------------

const CandidatesTab = ({
  candidates,
  summaryInProgress,
  selectedIndex,
  onSearchClose,
}: {
  candidates: any[];
  summaryInProgress: boolean;
  selectedIndex: number;
  onSearchClose: () => void;
}) => (
  <>
    {!candidates?.length && NO_RESULTS}
    <Flex w="100%">
      <Flex flex="1" flexDirection="column">
        {candidates?.map((candidate: any, index: number) => (
          <CandidateItem
            key={candidate.id}
            item={candidate}
            isRecentViewed={false}
            summaryInProgress={summaryInProgress}
            isSelected={index === selectedIndex}
            jobs={candidate?.job_application_details || []}
            onSearchClose={onSearchClose}
          />
        ))}
      </Flex>
    </Flex>
  </>
);

const JobsTab = ({
  jobs,
  selectedIndex,
  onSearchClose,
}: {
  jobs: any[];
  selectedIndex: number;
  onSearchClose: () => void;
}) => (
  <>
    {!jobs?.length && NO_RESULTS}
    {jobs?.map((job: any, index: number) => (
      <JobItem
        key={job.id}
        item={job}
        isSelected={index === selectedIndex}
        onSearchClose={onSearchClose}
      />
    ))}
  </>
);

const InterviewsTab = ({
  interviews,
  selectedIndex,
  onSearchClose,
}: {
  interviews: any[];
  selectedIndex: number;
  onSearchClose: () => void;
}) => (
  <>
    {!interviews?.length && NO_RESULTS}
    {interviews?.map((interview: any, index: number) => (
      <InterviewItem
        key={interview.id}
        item={interview}
        isSelected={index === selectedIndex}
        onSearchClose={onSearchClose}
      />
    ))}
  </>
);

const FlowsTab = ({
  flows,
  isLoading,
  selectedIndex,
  onSearchClose,
}: {
  flows: any[] | null;
  isLoading: boolean;
  selectedIndex: number;
  onSearchClose: () => void;
}) => {
  if (isLoading) {
    return (
      <KCenterSpinner
        h="100%"
        spinnerProps={{ width: '40px', height: '40px' }}
      />
    );
  }

  return (
    <>
      {flows?.map((flow: any, index: number) => (
        <FlowItem
          key={flow.id}
          item={flow}
          isSelected={index === selectedIndex}
          onSearchClose={onSearchClose}
        />
      ))}
      {(!flows || flows.length === 0) && NO_RESULTS}
    </>
  );
};
